import React, { useState, useEffect } from 'react';
import { Modal, List, Button, Popconfirm, message, Typography, Tag, Space, Pagination } from 'antd';
import { DeleteOutlined, PictureOutlined, VideoCameraOutlined, FileTextOutlined } from '@ant-design/icons';
import { getAuthMethod, deleteAuthMethod } from '../../services/HttpServices';

const { Text } = Typography;

const MediaManagerModal = ({ visible, onClose }) => {
  const [mediaList, setMediaList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({ current: 1, pageSize: 10, total: 0 });

  useEffect(() => {
    if (visible) {
      fetchMedia(1, 10);
    }
  }, [visible]);

  const fetchMedia = async (page = 1, size = 10) => {
    setLoading(true);
    try {
      const res = await getAuthMethod(`/api/media?page=${page - 1}&size=${size}&sort=createdDate,desc`);
      if (res && res.data) {
        setMediaList(res.data.content);
        setPagination({
          current: page,
          pageSize: size,
          total: res.data.totalElements,
        });
      }
    } catch (error) {
      message.error("Failed to load media files.");
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      await deleteAuthMethod(`/api/media/${id}`);
      message.success("Media deleted successfully.");
      // Refresh list
      fetchMedia(pagination.current, pagination.pageSize);
    } catch (error) {
      const errMsg = error.response?.data?.message || "Failed to delete media.";
      message.error(errMsg);
    }
  };

  const getMediaIcon = (type) => {
    switch (type) {
      case 'IMAGE':
        return <PictureOutlined className="tw-text-blue-500 tw-text-xl" />;
      case 'VIDEO':
        return <VideoCameraOutlined className="tw-text-purple-500 tw-text-xl" />;
      default:
        return <FileTextOutlined className="tw-text-gray-500 tw-text-xl" />;
    }
  };

  const formatSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const handleTableChange = (page) => {
    fetchMedia(page, pagination.pageSize);
  };

  return (
    <Modal
      title={
        <div className="tw-font-inter tw-font-bold tw-text-base tw-text-slate-800">
          Manage Media Storage
        </div>
      }
      open={visible}
      onCancel={onClose}
      footer={[
        <Button
          key="close"
          onClick={onClose}
          className="!tw-h-[36px] !tw-rounded-xl tw-font-semibold tw-border-slate-200 hover:tw-bg-slate-50 tw-text-slate-700 tw-px-5"
        >
          Close
        </Button>
      ]}
      width={620}
      centered
      destroyOnClose
    >
      <div className="tw-py-2 tw-font-inter">
        <div className="tw-max-h-[420px] tw-overflow-y-auto tw-pr-1.5 sleek-scrollbar">
          <List
            loading={loading}
            itemLayout="horizontal"
            dataSource={mediaList}
            pagination={false}
            locale={{ emptyText: 'No media files found.' }}
            renderItem={(item) => (
              <List.Item
                actions={[
                  <Popconfirm
                    title="Delete Media"
                    description="Are you sure you want to delete this media file? It cannot be undone."
                    onConfirm={() => handleDelete(item.id)}
                    okText="Yes, Delete"
                    cancelText="Cancel"
                    okButtonProps={{ danger: true }}
                  >
                    <Button type="text" danger icon={<DeleteOutlined />} title="Delete" className="hover:!tw-bg-rose-50 !tw-rounded-lg" />
                  </Popconfirm>
                ]}
                className="hover:tw-bg-slate-50 tw-transition-colors tw-rounded-xl tw-px-3 tw-py-2.5"
              >
                <List.Item.Meta
                  avatar={<div className="tw-mt-1">{getMediaIcon(item.mediaType)}</div>}
                  title={
                    <div className="tw-w-[360px]">
                      <Text ellipsis={{ tooltip: item.fileName }} className="tw-font-semibold tw-text-slate-800 tw-text-xs md:tw-text-sm">
                        {item.fileName}
                      </Text>
                    </div>
                  }
                  description={
                    <Space size="middle" className="tw-text-xs tw-text-slate-500">
                      <span>{formatSize(item.fileSize)}</span>
                      <Tag bordered={false} color="default" className="!tw-text-[10px] !tw-leading-[18px] !tw-bg-slate-100 !tw-text-slate-600 !tw-rounded-md">
                        {item.mimeType}
                      </Tag>
                    </Space>
                  }
                />
              </List.Item>
            )}
          />
        </div>

        {pagination.total > pagination.pageSize && (
          <>
            <div className="tw-h-px tw-w-full tw-bg-slate-100 tw-mt-3 tw-mb-2" />
            <div className="tw-flex tw-items-center tw-justify-between">
              <span className="tw-text-xs tw-text-slate-400">
                Showing {Math.min(pagination.total, (pagination.current - 1) * pagination.pageSize + 1)}-{Math.min(pagination.total, pagination.current * pagination.pageSize)} of {pagination.total} files
              </span>
              <Pagination
                current={pagination.current}
                pageSize={pagination.pageSize}
                total={pagination.total}
                onChange={handleTableChange}
                showSizeChanger={false}
                size="small"
              />
            </div>
          </>
        )}
      </div>
    </Modal>
  );
};

export default MediaManagerModal;
